/** @format */

import { useRef, useState, useMemo, useContext, useCallback, DragEvent } from "react";
import { getSession, setFileInfoData } from "../sessionStorage/sessionStorageMethod";
import { initFileSessionData } from "../constance/sessionData";
import { Context } from "../stores/Provider";
import { validationPattern } from "../validators/publicValidator";
import { PostMethod } from "../api/Api";
import { setIsLoading } from "../function/setIsLoading";
import { InquiryFileItem } from "../components/ui-parts/ocnk";
import { logoutAbnormalFunction } from "../function/logoutAbnormalFunction";
import {
  initApiModelType,
  initFileSessionDataType,
  fileRequestType,
  fileResponseType,
  fileDeleteRequestType,
  sessionFileType,
} from "../constance/typeModel";
import { initApiResponse } from "../constance/constance";

const useFileInputHooks = ({
  maxsize,
  id,
  multiple,
  page,
  accept,
  navigateByAgent,
}: {
  maxsize: number;
  id: string;
  multiple?: boolean;
  page: string;
  accept: string[];
  navigateByAgent: Function;
}) => {
  const { state, dispatch } = useContext(Context);
  const userInfoData = getSession("user");
  const inputFileRef = useRef<HTMLInputElement>(null);

  //コンテキスト抽出
  const fileContextState = useMemo(() => {
    if (state.entry.files === null || state.entry.files === undefined || state.entry.files === initFileSessionData)
      return getSession("files");
    return state.entry.files;
  }, [state.entry.files]);

  const acceptText = useMemo(() => {
    let acceptText: string = "";
    acceptText = accept.join(",");
    return acceptText;
  }, [accept]);

  const setFileDataHandler = (data: initFileSessionDataType) => {
    setFileInfoData(data);
    dispatch({
      type: "set-entry-files",
      payload: data,
    });
  };
  const [fileState, setFileState] = useState(fileContextState[id]);
  const [dragOverFlg, setDragOverFlg] = useState(false);

  const maxSizeByte = 1024 * 1024 * maxsize;
  const newArrayFileData = useMemo(() => {
    const newArrayFileData: sessionFileType[] | [] =
      fileContextState[id].length !== 0 ? Object.values(fileContextState[id]) : [];
    return newArrayFileData;
  }, [fileContextState, id]);

  const DragOverHandler = (e: DragEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setDragOverFlg(true);
  };
  const DragLeaveHandler = (e: DragEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setDragOverFlg(false);
  };
  const FileDropHandler = (e: DragEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setDragOverFlg(false);
    var files = e.dataTransfer.files;
    if (inputFileRef.current === null) return;
    inputFileRef.current.files = files;
    LoadFile(e);
  };
  const LoadFileHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    LoadFile(e);
    //同じファイルを登録->消去->登録ができないバグを解消する
    setTimeout(() => setNullValue(e), 1000);
  };

  const setNullValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e === undefined || e === null || e.target === undefined) return;
    e.target.value = "";
  };

  const DeteleFileHandler = useCallback(
    async (index: number) => {
      let response: initApiModelType<undefined> = initApiResponse;
      const newFiles = newArrayFileData;
      const postData = {
        file_method: "delete",
        entry_id: userInfoData.entry_id,
        files: {
          ...initFileSessionData,
          [id]: [{ file_id: newFiles[index].file_id }],
        },
      };
      //ローディング開始
      setIsLoading(true, dispatch);
      response = await PostMethod<fileDeleteRequestType, undefined>(postData, "/file_data");
      //エラー時ハンドラ
      if (response.code !== 200) {
        if (response.code === 401)
          return logoutAbnormalFunction(response, dispatch, navigateByAgent, state.agentPath, state.zAccount);
        return navigateByAgent("/system_error");
      }
      newFiles.splice(index, 1);
      setFileDataHandler({ ...fileContextState, [id]: newFiles });
      setFileState(newFiles);
      //ローディング終了
      setIsLoading(false, dispatch);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [dispatch, fileContextState, id, navigateByAgent, newArrayFileData, userInfoData.entry_id],
  );

  const thumbs = useMemo(() => {
    const thumb = newArrayFileData.map((file: sessionFileType, index: number) => (
      <InquiryFileItem key={index} file={file} deleteFile={() => DeteleFileHandler(index)} upload entry />
    ));
    return thumb;
  }, [DeteleFileHandler, newArrayFileData]);

  /**
   * 拡張子取得（最後の "." 以降）
   */
  const getExtension = (filename: string): string => {
    const lastDot = filename.lastIndexOf(".");
    if (lastDot === -1 || lastDot === filename.length - 1) return "";
    return filename.slice(lastDot + 1).toLowerCase();
  };

  /**
   * ファイル名から最後の拡張子だけ除去（例: a.b.c.png -> a.b.c）
   */
  const removeLastExtension = (filename: string): string => {
    const lastDot = filename.lastIndexOf(".");
    if (lastDot === -1) return filename;
    return filename.slice(0, lastDot);
  };

  /**
   * 拡張子配列を正規化（大文字/ドット有無を吸収）
   * ["PDF","png",".JPG"] -> ["pdf","png","jpg"]
   */
  const normalizeExtensions = (exts: string[]) => exts.map((x) => x.trim().toLowerCase().replace(/^\./, ""));

  /**
   * MIME type 正規化
   */
  const normalizeMime = (mime: string) => (mime ?? "").trim().toLowerCase();

  type ValidateResult = { ok: boolean; reason?: "ext" | "mime" };

  /**
   * 拡張子 + MIME 併用チェック
   *
   * 方針（実運用向け）:
   * - 拡張子は必須で一致
   * - MIME が取得できるなら MIME も一致を要求
   * - MIME が空("")の場合は、拡張子OKなら通す
   */
  const validateFileExtAndMime = (file: File, allowedExts: string[], allowedMimes: string[]): ValidateResult => {
    const ext = getExtension(file.name);
    const mime = normalizeMime(file.type);

    const exts = normalizeExtensions(allowedExts);
    const mimes = allowedMimes.map((x) => x.toLowerCase());

    const extOk = exts.includes(ext);
    if (!extOk) return { ok: false, reason: "ext" };

    // MIME が空の環境がある（ドラッグ&ドロップ/一部ブラウザ等）
    if (!mime) return { ok: true };

    const mimeOk = mimes.includes(mime);
    if (!mimeOk) return { ok: false, reason: "mime" };

    return { ok: true };
  };

  /**
   * ファイルをロード
   */
  const LoadFile = async (e: React.ChangeEvent<HTMLInputElement> | DragEvent) => {
    if (inputFileRef.current === null) return;
    let insertFileData = fileState;
    let maxFileNumber = 0;
    const inputFiles = inputFileRef.current.files;
    let sumFileSize = 0;

    if (inputFiles === null) return;

    //ファイルサイズ計算＋ファイル連番最大値取得（API通信前のファイル）
    insertFileData.forEach((file: sessionFileType) => {
      //ファイルサイズ計算
      sumFileSize += file.file_size;
      //ファイル連番最大値取得
      const base = file.file_name ? removeLastExtension(file.file_name) : "";
      const compareNumber = base ? Number(base.split("_").pop()) : 0;
      if (compareNumber > maxFileNumber) maxFileNumber = compareNumber;
    });

    const filesArray = Array.from(inputFiles);

    const addFilesTotalSize = filesArray.reduce((acc, file) => acc + file.size, 0);
    if (sumFileSize + addFilesTotalSize > maxSizeByte) {
      alert(`ファイルの登録に失敗しました。アップロードできるファイルの合計サイズは最大${maxsize}MBまでです。`);
      setIsLoading(false, dispatch);
      return;
    }

    //ファイルを一つずつ登録
    await Promise.all(
      filesArray.map(async (file: File) => {
        const filePromise = new Promise<{ file_name_origin: string; file_size: number; file_data: string } | null>(
          (resolve) => {
            // ===== 拡張子 + MIME バリデーション =====
            const result = validateFileExtAndMime(file, validationPattern.file, validationPattern.fileMimeType);
            if (!result.ok) {
              const message =
                result.reason === "mime"
                  ? "ファイル形式が不正です。png, jpg, jpeg, pdf以外は登録できません。"
                  : "png, jpg, jpeg, pdf以外は登録できません。";

              dispatch({
                type: `set-validate-${page}`,
                payload: {
                  ...state.validateMessages[page],
                  [id]: { message },
                },
              });
              return resolve(null);
            } else {
              dispatch({
                type: `set-validate-${page}`,
                payload: {
                  ...state.validateMessages[page],
                  [id]: { message: "" },
                },
              });
            }

            const fr = new FileReader();
            fr.onload = async (ev) => {
              maxFileNumber++;

              const resultStr = ev.target?.result;
              const fileData =
                typeof resultStr === "string" && resultStr.includes("base64,") ? resultStr.split("base64,")[1] : "";

              const tempFileData = {
                file_name_origin: file.name,
                file_size: file.size,
                file_data: fileData,
              };
              resolve(tempFileData);
            };
            fr.readAsDataURL(file);
          },
        );
        return filePromise;
      }),
    ).then(async (responseArray) => {
      // null を除去（拡張子NGが混ざっていてもOKファイルだけ送る）
      const filtered = (responseArray as any[]).filter(Boolean);

      // 送るものがないなら API 呼ばない
      if (filtered.length === 0) {
        setIsLoading(false, dispatch);
        return;
      }

      let response: initApiModelType<fileResponseType> = initApiResponse;
      const postData = {
        entry_id: userInfoData.entry_id,
        file_method: "register",
        files: {
          ...initFileSessionData,
          [id]: filtered,
        },
      };
      //ローディング開始
      setIsLoading(true, dispatch);
      response = await PostMethod<fileRequestType, fileResponseType>(postData, "/file_data");
      //ローディング終了
      setIsLoading(false, dispatch);
      // ファイル登録失敗時の挙動
      if (response.code !== 200 || response.response_data === undefined) {
        if (response.code === 401)
          return logoutAbnormalFunction(response, dispatch, navigateByAgent, state.agentPath, state.zAccount);
        return navigateByAgent("/system_error");
      }
      // ファイル登録成功時の挙動
      insertFileData = insertFileData.concat(response.response_data.files);
      setFileState(insertFileData);
      setFileDataHandler({ ...fileContextState, [id]: insertFileData });
    });
  };
  return {
    dragOverFlg,
    setDragOverFlg,
    thumbs,
    DragOverHandler,
    DragLeaveHandler,
    FileDropHandler,
    LoadFileHandler,
    inputFileRef,
    fileContextState,
    acceptText,
  };
};

export default useFileInputHooks;
